import React, { useContext, useEffect, useState } from "react";
import { GalleryItem } from "helpers/types";
import { Link } from "react-router-dom";
import GalleryImage from "pages/cases/view/GalleryImage";
import { getImageFilename, getTimeIn12h, getMediaFileDisplayNameDependingOnContext } from "helpers/utils";
import classNames from "classnames";
import { createPortal } from "react-dom";
import { Spinner, Button } from "react-bootstrap";
import CameraMediaApi from "api/camera-media-api";
import ImagePopupModal from "pages/cases/view/ImagePopupModal";
import CaseApi from "api/case-api";
import { CaseImagesContext } from "pages/cases/view/case-images/CaseImages";
import { ApiError } from "api/backend-api";



type IProps = {
    name: string;
    images: any[];
    onVideoDeleted?: () => void;
    // Create Video tab only: runs the same assign action as the toolbar button
    onAssignVideoImages?: () => void;
}



const mediaApi = new CameraMediaApi();
const caseApi = new CaseApi();

const ImageGallery = (props: IProps) => {
    const {
        selectImageIds,
        setSelectImageIds,
        lockedImageIds,
        setLockedImageIds,
        caseInfo,
        locationInfo,
        setLocationInfo,
        captureDate,
        contextMenuContainer,
        onShowToastMessage: showToastMessage,
        imageRequestHD,
        currentViewImageIndex,
        setCurrentViewImageIndex,
        roiPopupImage,
        showRoiModal,
        setShowRoiModal,
        onHideRoiModal,
        openLightBox,
        images,
        setImages,
        getImageSrc, 
        imageSequenceNumberToShowInPopup,
        setImageSequenceNumberToShowInPopup,
        // From context: handler that opens an image in a new tab, passed down to the popup below
        onGoToImageInNewTab,
        showGoToImageLoadingAnim,
        setShowGoToImageLoadingAnim,
        videoCreationImageSetImages,
        setVideoCreationImageSetImages,
        caseImagesAreWeOnApageWhereSelectRibbonShouldShow,
        isImageInCreateVideoImageSet,
        getImageCornerRibbonMarkup,
        singeImageCheckboxHandlerCreateVideoPage,
        isSelectImage,
        imageIsPrimaryCheckboxChecked,
        mediaFileRenamed,
        setMediaFileRenamed
    } = useContext(CaseImagesContext);

    const [isSavingRoi, setIsSavingRoi] = useState(false);

    const [isPreparingDownload, setIsPreparingDownload] = useState(false);

    const [isDeletingVideo, setIsDeletingVideo] = useState(false);

    // initialize things
    useEffect(() => {
        setImages([])
        setCurrentViewImageIndex(0)
        setShowRoiModal(false)
    }, [])

    useEffect(() => {
        setImages([...props.images])
    }, [props.images]);



    const onImageDownloaded = (idx: number, imageData: string, downloadURL: string) => {
        const image = images[idx]
        if (image === undefined) {
            return;
        }
        if (image.hd_file_path !== null) {
            image.filename = image.filename.replace('sd', 'hd')
        }

        image.download_url = downloadURL
        image.image_src = imageData

        setImages((prevImages: GalleryItem[] ) => {
            // A download that finished after its page was swapped out must not write into
            // the replacement page's array — past its end that stretched the array with
            // undefined holes (the go-to "Error!" crash), within it it planted a stale image
            if (prevImages[idx] !== image) {
                return prevImages;
            }
            const updatedImages = [...prevImages]
            updatedImages[idx] = image
            return updatedImages
        })
    }

    // Open the go-to popup once the target image is present on the loaded page AND decoded.
    // State-driven (deps below), so it always sees the latest images — no stale closure — and works
    // whether the target just downloaded or was already loaded on the page.
    useEffect(() => {
        if (typeof imageSequenceNumberToShowInPopup !== 'number' || imageSequenceNumberToShowInPopup <= -1) {
            return;
        }
        const idx = images.findIndex((img: GalleryItem) => img && parseInt(img.sequence) === imageSequenceNumberToShowInPopup);
        if (idx === -1 || !images[idx].image_src) {
            return; // target not on the loaded page yet, or not finished downloading — wait for the next change
        }
        openLightBox(idx, images[idx].image_src)
        setImageSequenceNumberToShowInPopup(-1)
        setShowGoToImageLoadingAnim(false)
    }, [imageSequenceNumberToShowInPopup, images]);





    const singleImageCheckboxHandler = (imageId: number) => {
        if (props.name === 'Create Video') {
            singeImageCheckboxHandlerCreateVideoPage(imageId);
        } else {
            onImageSelected(imageId);
        }
    }

    const onImageSelected = (imageId: number) => {
        setSelectImageIds((prevIds: number[]) => {
            const updatedIds = [...prevIds]
            if (!isSelectImage(imageId)) {
                updatedIds.push(imageId);
            } else {
                const indx = updatedIds.findIndex(id => id === imageId);
                updatedIds.splice(indx, 1);
            }
            return [...updatedIds]
        })
    }



    const requestHD = async (index: number, imageId: number) => {
        imageRequestHD(index, imageId, images, setImages, mediaApi);
    };

    const toggleMediaLockStatus = async (imageId: number) => {
        if ( lockedImageIds.includes(imageId) ) {
            try {
                let api_resp = await mediaApi.markMediaAsUnlocked(imageId);
                

                if (api_resp.exit_code < 0){
                    showToastMessage && showToastMessage('There was a problem unlocking image');
                    return;
                }

                setLockedImageIds((prevIds: number[]) => {
                    const newIds = prevIds.filter((id) => id !== imageId);
                    return newIds;
                });
            } catch (e) {
                console.error(e)
            } 
        } else {
            try {
                let api_resp = await  mediaApi.markMediaAsLocked(imageId);

                if (api_resp.exit_code < 0){
                    showToastMessage && showToastMessage('There was a problem locking image');
                    return;
                }

                setLockedImageIds((prevIds: number[]) => {
                    const newIds = [...prevIds, imageId];
                    return newIds;
                });
            } catch (e) {
                console.error(e)
            } 
        }
    }

    const deleteVideo = async (imageId: number) => {
        if (!window.confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
            return;
        }

        try {
            setIsDeletingVideo(true);
            await mediaApi.deleteMedia(imageId);
            
            // Remove the deleted video from the images array
            setImages((prevImages: GalleryItem[]) => {
                return prevImages.filter((image) => image.id !== imageId);
            });

            // Call the callback to refresh the parent component's data
            if (props.onVideoDeleted) {
                props.onVideoDeleted();
            }

            showToastMessage && showToastMessage('Video deleted successfully', 'success');
        } catch (e) {
            console.error('Error deleting video:', e);
            showToastMessage && showToastMessage('Error deleting video', 'danger');
        } finally {
            setIsDeletingVideo(false);
        }
    }

    const renameVideo = async (imageId: number) => {
        const image = images.find((img: GalleryItem) => img.id === imageId);
        if (!image) {
            showToastMessage && showToastMessage('Video not found', 'danger');
            return;
        }

        // Extract current filename without extension for display
        const currentName = image.filename || '';
        let nameWithoutExt = currentName.replace(/\.[^/.]+$/, '');
        
        // remove mac address portion
        if (nameWithoutExt.indexOf('_') > -1) {
            nameWithoutExt = nameWithoutExt.substring( nameWithoutExt.indexOf('_') + 1 )
        }
        
        // Prompt user for new name
        const newName = window.prompt('Enter a new name for the video:', nameWithoutExt);
        if (!newName || newName.trim() === '') {
            return; // User cancelled or entered empty name
        }

        try {
            const response = await mediaApi.renameMedia(imageId, newName.trim());
            
            if (response.filename) {
                // Update the image in the local state
                setImages((prevImages: GalleryItem[]) => {
                    return prevImages.map((myImg) => {
                        if (myImg && myImg.id === imageId) {
                            return {...myImg, filename: response.filename} 
                        } else {
                            return myImg;
                        } 
                    })
                });
                setMediaFileRenamed((prevValue: number) => {
                    return (prevValue + 1) % 32
                })

                showToastMessage && showToastMessage('Video renamed successfully', 'success');
            } else {
                showToastMessage && showToastMessage('There was a problem renaming the video', 'danger');
            }
        } catch (e) {
            let additional_error_message = '';
            console.error('Error renaming video:', e);
            // perhaps show e.data.error? // 20251111 1328
            if (e instanceof ApiError && e.data.error ) {
                additional_error_message = 'Error: ' + e.data.error;
            }
            showToastMessage && showToastMessage('Error renaming video. ' + additional_error_message, 'danger');
        }
    }

    const isImageMarkedAsLocked = (imageId: number) => {
        let isLocked = false;
        if (lockedImageIds && lockedImageIds.includes(imageId)){
            isLocked = true;
        }
        return isLocked;
    }

    async function onSaveRegionsOfInterest(regions: any) {
        try {
            setIsSavingRoi(true);
            const location = await caseApi.updateLocation(caseInfo.id, locationInfo.id, {
                regions_of_interest: regions
            });

            setLocationInfo({...location})
        } catch (e) {
            console.error(e)
        } finally {
            setIsSavingRoi(false)
        }
    }

    const getImageInfo = (index: number) => {
        return images[index];
    }

    const onViewNextImage = () => {
        if (currentViewImageIndex === images.length - 1) {
            return;
        }
        setCurrentViewImageIndex(currentViewImageIndex + 1)
    }

    const onViewPreviousImage = () => {
        if (currentViewImageIndex <= 0) {
            return;
        }
        setCurrentViewImageIndex(currentViewImageIndex - 1)
    }

    const areWeOnApageWhereSelectRibbonShouldShow = () => {
        return caseImagesAreWeOnApageWhereSelectRibbonShouldShow(props.name)
    }

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent): void => {
            if (event.keyCode === 39) {
                onViewNextImage();
            } else if (event.keyCode === 37) {
                onViewPreviousImage();
            }
        };

        document.addEventListener('keydown', handleKeyPress);

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [currentViewImageIndex, images.length]);




    const createDownloadLink = (url: string) => {
        const a = document.createElement('a');
        a.href = url;
	let filename:string = `${captureDate}-${props.name.toUpperCase()}-${caseInfo.name}-${locationInfo?.name}.zip`;
	// Avoid whitespace and colons in filenames
        a.download = filename.replace(/[:\s]+/g,"_");
        return a
    }

    const downloadAlbum = async () => {
        try {
            setIsPreparingDownload(true)
            const mediaIds = images.map((image: GalleryItem) => image.id)
            if (images.length === 0) {
                showToastMessage && showToastMessage("No images to download");
                return;
            }
            const filterHd = props.name == 'HD'
            const includeHDimagesIfExists = props.name === 'Select'
            const zipBlob = await mediaApi.downloadMedia(caseInfo.id, mediaIds, filterHd, includeHDimagesIfExists);
            const blobUrl = URL.createObjectURL(zipBlob);
            const downloadLink = createDownloadLink(blobUrl);
            downloadLink.click();
            URL.revokeObjectURL(blobUrl)

        } catch (e) {
            console.log("Exception when downloading album: " + e);
            showToastMessage && showToastMessage("Error preparing download", "danger");
        } finally {
            setIsPreparingDownload(false);
        }
    };

    const getGridImageCornerRibbonMarkupConditionally = (imageId: number) => {
        if (areWeOnApageWhereSelectRibbonShouldShow() && isSelectImage(imageId)) {
            return getImageCornerRibbonMarkup();
        } else if (props.name === 'Create Video' && isImageInCreateVideoImageSet(imageId)) {
            return getImageCornerRibbonMarkup('For Video');
        }
        return null;
    }

    //console.log('props name is ' + props.name)

    return (
        <React.Fragment>
            {
                contextMenuContainer?.current && props.name !== 'All' &&
                createPortal(
                    <React.Fragment>
                        {
                            isPreparingDownload ? <Spinner size="sm" className="text-warning"/>
                                : (
                                    images.length > 0 &&
                                    <Link to="#"
                                          className="filter-menu-item position-absolute"
                                          onClick={downloadAlbum}>
                                                <span
                                                    className="material-symbols-outlined album-download-icon ">download</span>
                                    </Link>
                                )
                        }
                    </React.Fragment>,
                    contextMenuContainer.current,
                    "album-download"
                )
            }

            {/* Image Gallery */}
            <div className="folder-list-container ">
                {
                    images.map((image: GalleryItem, idx: number) => {
                        if (!image ) {
                            return;
                        }
                        if (!props.images.includes(image)) {
                            return;
                        }
                        return (
                            <React.Fragment key={image.id}>
                                <div className="gallery-image-container ribbon-box">
                                    {
                                        getGridImageCornerRibbonMarkupConditionally(image.id)
                                    }

                                    <Link
                                        to="#"
                                        className="image-popup"
                                        title={image.filename}>
                                        <GalleryImage
                                            onImageDownloaded={
                                                (downloadURL: string,
                                                 dataUrl: string
                                                ) => onImageDownloaded(idx, dataUrl, downloadURL)
                                            }
                                            src={getImageSrc(idx)}
                                            onClick={(imageSrc: string) => openLightBox(idx, imageSrc)}
                                            media={image}
                                            className="img-fluid img"
                                            />
                                    </Link>
                                    <div className="gallery-image-info">
                                        <h4 className="font-16 mt-0">{getMediaFileDisplayNameDependingOnContext(image, props.name)}</h4>
                                        <Link to="#">
                                            <span
                                                className="text-muted ms-1">{getTimeIn12h(image.captured_at)}</span>
                                        </Link>
                                        <div className="gallery-img-btns">
                                            <Link to="#">
                                                {image.hd_request_status === "PENDING" ? (
                                                        <span className="hd-pending text-warning">
                                                            <span className="material-symbols-outlined">hd</span>
                                                        </span>
                                                    ) :
                                                    (<span
                                                        onClick={() => requestHD(idx, image.id)}
                                                        className={classNames("material-symbols-outlined",
                                                            {
                                                                "text-muted": image.hd_file_path === null,
                                                                "text-success": image.hd_file_path !== null
                                                            })}>hd
                                                </span>)}
                                            </Link>
                                            <Link to={image["download_url"]}
                                                  target="_target"
                                                  download={getImageFilename(image)}>
                                                <span className={classNames("material-symbols-outlined",
                                                    {
                                                        "text-primary": image["download_url"] !== undefined,
                                                        "text-muted": image["download_url"] === undefined
                                                    })}>download</span>
                                            </Link>
                                            <Link to="#">
                                                <span
                                                    onClick={() => singleImageCheckboxHandler(image.id)}
                                                    className={classNames("material-symbols-outlined",
                                                        {
                                                            "text-muted": !imageIsPrimaryCheckboxChecked(image.id, props.name),
                                                            "text-success": imageIsPrimaryCheckboxChecked(image.id, props.name)
                                                        })}>
                                                        {
                                                            imageIsPrimaryCheckboxChecked(image.id, props.name) ? "select_check_box" : "check_box_outline_blank"
                                                        }
                                                </span>
                                            </Link>
                                            <Link to="#">
                                                <span
                                                    onClick={() => toggleMediaLockStatus(image.id)}
                                                    className={classNames("material-symbols-outlined", "lock_unlock_media_icon",
                                                        {
                                                            "text-muted": !isImageMarkedAsLocked(image.id),
                                                            "text-success": isImageMarkedAsLocked(image.id),
                                                        })}>{lockedImageIds.includes(image.id) ? "lock" : "lock_open"}
                                                </span>
                                            </Link>
                                            {props.name === 'Video' && (
                                                <Link to="#">
                                                    <span
                                                        onClick={() => renameVideo(image.id)}
                                                        className={classNames("material-symbols-outlined", "rename_video_icon")}>
                                                        edit
                                                    </span>
                                                </Link>
                                            )}
                                            {props.name === 'Video' && (
                                                <Link to="#">
                                                    <span
                                                        onClick={() => deleteVideo(image.id)}
                                                        className={classNames("material-symbols-outlined", "delete_video_icon",
                                                            {
                                                                "text-danger": !isDeletingVideo,
                                                                "text-muted": isDeletingVideo,
                                                            })}>
                                                        delete
                                                    </span>
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </React.Fragment>
                        )
                    })
                }
            </div>

            {/* Image popup */}
            {roiPopupImage &&
                <ImagePopupModal
                    show={showRoiModal}
                    isSaving={isSavingRoi}
                    onNext={onViewNextImage}
                    onPrev={onViewPreviousImage}
                    onHdRequested={requestHD}
                    onSaveRegionsOfInterest={onSaveRegionsOfInterest}
                    regions={locationInfo.regions_of_interest || []}
                    imageIndex={currentViewImageIndex}
                    imageSrc={getImageSrc(currentViewImageIndex)}
                    imageInfo={getImageInfo(currentViewImageIndex)}
                    isSelect={isSelectImage(getImageInfo(currentViewImageIndex).id)}
                    singleImageCheckboxHandler={singleImageCheckboxHandler}
                    onGoToImageInNewTab={onGoToImageInNewTab}
                    toggleMediaLockStatus={toggleMediaLockStatus}
                    isImageMarkedAsLocked={isImageMarkedAsLocked}
                    onHide={onHideRoiModal}
                    galleryName={props.name}
                    onAssignVideoImages={props.onAssignVideoImages}
                    />
            }
        </React.Fragment>
    );
}

export default React.memo(ImageGallery);